import React from 'react';
import {Box, Typography, Paper, Chip, Stack} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import {colors} from '../../../theme/colors';

interface CalendarEventContext {
  eventId: string;
  title: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  attendees?: string[];
  status?: 'confirmed' | 'tentative' | 'cancelled';
  conferenceUrl?: string;
}

interface Props {
  calendarEvent: CalendarEventContext;
}

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  confirmed: 'success',
  tentative: 'warning',
  cancelled: 'error',
};

export const CalendarEventPanel: React.FC<Props> = ({calendarEvent}) => {
  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Paper variant="outlined" sx={{mx: 2, mb: 2, overflow: 'hidden'}}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: colors.blue[50],
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CalendarMonthIcon sx={{fontSize: 20, color: colors.blue[700]}} />
        <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1}}>
          Calendar Event
        </Typography>
        {calendarEvent.status && (
          <Chip
            size="small"
            label={calendarEvent.status}
            color={STATUS_COLOR[calendarEvent.status] ?? 'default'}
          />
        )}
      </Box>

      <Box sx={{p: 2}}>
        <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
          {calendarEvent.title}
        </Typography>

        <Stack spacing={1}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <EventIcon sx={{fontSize: 18, color: 'text.secondary'}} />
            <Typography variant="body2" color="text.secondary">
              {formatDate(calendarEvent.startTime)}
              {calendarEvent.endTime ? ` — ${formatDate(calendarEvent.endTime)}` : ''}
            </Typography>
          </Box>

          {calendarEvent.location && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <LocationOnIcon sx={{fontSize: 18, color: 'text.secondary'}} />
              <Typography variant="body2" color="text.secondary">
                {calendarEvent.location}
              </Typography>
            </Box>
          )}

          {calendarEvent.attendees && calendarEvent.attendees.length > 0 && (
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
              <PeopleIcon sx={{fontSize: 18, color: 'text.secondary', mt: 0.25}} />
              <Box>
                {calendarEvent.attendees.map((email) => (
                  <Typography key={email} variant="body2" color="text.secondary">
                    {email}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {calendarEvent.conferenceUrl && (
            <Typography variant="body2">
              <a
                href={calendarEvent.conferenceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Join video call
              </a>
            </Typography>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};
